// src/pages/InterviewDetails.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";
import Layout from "../components/Layout";
import { fetchCandidateStatus } from "../api/hrStatusClient"; // ✅ Use your helper
import FeedbackButton from "../components/FeedbackButton";

export default function InterviewDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [interview, setInterview] = useState({});
  const [rounds, setRounds] = useState([]);
  const [questionsMap, setQuestionsMap] = useState({});
  const [candidateStatus, setCandidateStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      console.error("❌ Missing interview ID");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const interviewRes = await api.get(`/interviews/${id}`);
        setInterview(interviewRes.data || {});

        const roundsRes = await api.get(`/interviews/${id}/rounds`);
        const roundsData = Array.isArray(roundsRes.data.rounds)
          ? roundsRes.data.rounds
          : [];
        setRounds(roundsData);

        const questionsPromises = roundsData.map(async (round) => {
          try {
            const qRes = await api.get(`/rounds/${round.id}/questions`);
            const questionsData = Array.isArray(qRes.data.questions)
              ? qRes.data.questions
              : qRes.data || [];
            return { roundId: round.id, questions: questionsData };
          } catch (err) {
            console.error(`⚠️ Error fetching questions for round ${round.id}:`, err);
            return { roundId: round.id, questions: [] };
          }
        });

        const allQuestions = await Promise.all(questionsPromises);
        const questionsObj = {};
        allQuestions.forEach(({ roundId, questions }) => {
          questionsObj[roundId] = questions;
        });
        setQuestionsMap(questionsObj);

        // ✅ Fetch candidate-facing status using your helper
        const statusData = await fetchCandidateStatus(id);
        setCandidateStatus(statusData);

        setLoading(false);
      } catch (err) {
        console.error("❌ Error fetching interview details:", err);
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleDeleteRound = async (roundId) => {
    if (!window.confirm("Are you sure you want to delete this round?")) return;

    try {
      await api.delete(`/rounds/${roundId}`);
      setRounds((prev) => prev.filter((r) => r.id !== roundId));
      const updatedMap = { ...questionsMap };
      delete updatedMap[roundId];
      setQuestionsMap(updatedMap);
      alert("✅ Round deleted.");
    } catch (err) {
      console.error("❌ Delete failed:", err);
      alert(err.response?.data?.message || "Delete failed.");
    }
  };

  const handleEditRound = (roundId) => {
    navigate(`/interviews/${id}/rounds/${roundId}/edit`);
  };

  const handleMarkComplete = async () => {
    if (!window.confirm("Mark this interview as completed?")) return;

    try {
      await api.patch(`/interviews/${id}/status`, { status: "Completed" });
      setInterview((prev) => ({ ...prev, status: "Completed" }));
      alert("✅ Marked as completed.");
    } catch (err) {
      console.error("❌ Failed to mark complete:", err);
      alert(err.response?.data?.message || "Failed to update.");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? "Invalid Date" : date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Layout>
        <h2 className="text-lg font-semibold">Loading Interview Details...</h2>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-bold">Interview: {interview.company || "N/A"}</h2>
          <h3 className="text-xl">Position: {interview.position || "N/A"}</h3>
          <h4 className="text-lg">
            Status:{" "}
            <span
              className={`px-2 py-1 rounded ${
                interview.status === "Completed"
                  ? "bg-green-200 text-green-800"
                  : "bg-yellow-200 text-yellow-800"
              }`}
            >
              {interview.status || "N/A"}
            </span>
          </h4>
          {/* ✅ Optional HR status toggle section */}
          {candidateStatus && (
            <div className="mt-3">
              <button
                onClick={() =>
                  setCandidateStatus((prev) => ({
                    ...prev,
                    _show: !prev._show, // toggle flag
                  }))
                }
                className="text-sm text-blue-600 hover:underline"
              >
                🔍 {candidateStatus._show ? "Hide" : "View"} HR Status Update
              </button>

              {candidateStatus._show && (
                <div className="mt-2 text-sm border rounded bg-gray-50 p-3">
                  <p>
                    <span className="font-medium">HR Status:</span>{" "}
                    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">
                      {candidateStatus.status || "Unknown"}
                    </span>
                  </p>
                  {candidateStatus.description && (
                    <p className="text-gray-600 mt-1">{candidateStatus.description}</p>
                  )}
                  {candidateStatus.updatedAt && (
                    <p className="text-xs text-gray-400 mt-1">
                      Last updated: {formatDate(candidateStatus.updatedAt)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <p className="text-gray-600 mt-2">Date: {formatDate(interview.date)}</p>
        </div>

        <div className="space-x-2">
          <button
            onClick={() => navigate(`/interviews/${id}/rounds/new`)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            ➕ Add Round
          </button>
          {interview.status !== "Completed" && (
            <button
              onClick={handleMarkComplete}
              className="bg-purple-700 text-white px-4 py-2 rounded hover:bg-purple-800"
            >
              ✅ Mark Complete
            </button>
          )}
        </div>
      </div>

      <h3 className="text-xl font-semibold mb-3">Rounds & Questions</h3>
      {!rounds?.length ? (
        <p className="text-gray-500">No rounds added yet.</p>
      ) : (
        rounds.map((round) => (
          <div key={round.id} className="mb-4 p-4 border rounded shadow-sm bg-gray-50">
            <div className="flex justify-between items-center">
              <h4 className="text-lg font-bold">{round.name}</h4>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditRound(round.id)}
                  className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => handleDeleteRound(round.id)}
                  className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                >
                  🗑 Delete
                </button>
              </div>
            </div>
            <p className="text-gray-600 mb-2">{round.description || "No description"}</p>

            {round.attachment && (
              <a
                href={`/${round.attachment}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 underline text-sm mb-2 inline-block"
              >
                📎 View Round Attachment
              </a>
            )}

            <button
              onClick={() => navigate(`/interviews/${id}/rounds/${round.id}/questions`)}
              className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 mb-3"
            >
              📋 Manage Questions
            </button>

            <h5 className="font-semibold">Questions:</h5>
            {questionsMap[round.id]?.length ? (
              <ul className="list-disc ml-6">
                {questionsMap[round.id].map((question) => (
                  <li key={question.id} className="mb-2">
                    {/* ✅ Render HTML properly */}
                    <p className="font-medium" dangerouslySetInnerHTML={{ __html: question.questionText }} />
                    {question.type && (
                      <p className="text-sm text-gray-600">Type: {question.type}</p>
                    )}
                    {question.difficulty && (
                      <p className="text-sm text-gray-600">Difficulty: {question.difficulty}</p>
                    )}
                    {question.notes && (
                      <p className="text-sm text-gray-500" dangerouslySetInnerHTML={{ __html: question.notes }} />
                    )}
                    {question.isImportant && (
                      <span className="inline-block text-xs text-red-500 font-bold">
                        ⭐ Important
                      </span>
                    )}
                    {question.attachment && (
                      <a
                        href={`/${question.attachment}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 underline text-sm"
                      >
                        📎 View Attachment
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500">No questions added for this round.</p>
            )}
          </div>
        ))
      )}

       <FeedbackButton interviewId={id} />
    </Layout>
  );
}
